/**
 * Promocoes (spec 067; Vitor, 02/11: "criar uma promocao de black friday [...] pode ser um valor nominal, ou um
 * percentual [...] um periodo, ou recorrente, ou unitario para clientes especificos"). Diferente do cupom, ninguem digita
 * nada: o preco ja aparece com desconto no checkout e nos planos a venda pra quem se encaixa. Vale nos planos marcados
 * (ou em todos), num periodo, toda semana ou todo ano, pra todos ou so pros CPFs da lista. Quem decide se vale, quanto
 * tira e a situacao e o servico de pagamentos; a tela so mostra e pergunta, com a previa do destaque do checkout.
 */
import {
  Aviso, Botao, CabecalhoDePagina, Campo, CampoCaixa, CampoDataHora, CampoDinheiro, CampoHora, CampoSelecao, CampoTextoLongo, Carregando, DestaqueDaPromocao, NOME_DO_TEMA, Pilha, Segmentado, Selo,
  TEMAS_DA_PROMOCAO, Tabela, Vazio, formatarMoeda, formatarReais, soDigitos, type Coluna,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { erroDe, useApi, usePapel } from "../api";
import { config } from "../config";
import { usePagina } from "../pagina";
import { corpoDaPromocao, dataHoraEmBrasilia, DIAS_DA_SEMANA, descontoDa, FORMULARIO_VAZIO, formularioDe, localParaIso, mascaraDdmm, quandoVale, type FormularioDaPromocao, type Promocao } from "../promocoes";
import { DialogoDeAcao } from "./DialogoDeAcao";

const PAG = (caminho: string) => `${config().PAGAMENTOS_URL.replace(/\/$/, "")}${caminho}`;
type Moeda = "BRL" | "USD";
type PlanoDoCatalogo = { slug: string; nome: string; ativo: boolean };

const SITUACAO: Record<string, { rotulo: string; tom: "sucesso" | "info" | "alerta" | "neutro" }> = {
  VALENDO: { rotulo: "Valendo agora", tom: "sucesso" },
  AGENDADA: { rotulo: "Agendada", tom: "info" },
  PAUSADA: { rotulo: "Pausada", tom: "alerta" },
  ENCERRADA: { rotulo: "Encerrada", tom: "neutro" },
};

/** "Todos os planos", o nome do plano, dois nomes, ou "N planos" (os nomes vao no apoio). */
function planosDa(p: Promocao, catalogo: PlanoDoCatalogo[]): { texto: string; apoio?: string } {
  if (p.todos_os_planos) return { texto: "Todos os planos" };
  const nomes = p.planos.map((s) => catalogo.find((c) => c.slug === s)?.nome ?? s);
  if (nomes.length <= 2) return { texto: nomes.join(" e ") };
  return { texto: `${nomes.length} planos`, apoio: nomes.join(", ") };
}

function CamposDaPromocao({ f, setF, moeda, catalogo, carregandoPlanos }: { f: FormularioDaPromocao; setF: (f: FormularioDaPromocao) => void; moeda: Moeda; catalogo: PlanoDoCatalogo[]; carregandoPlanos: boolean }) {
  const mudar = (parte: Partial<FormularioDaPromocao>) => setF({ ...f, ...parte });
  const percentual = Number(f.percentual);
  const desconto = f.tipo === "PERCENTUAL" ? (percentual >= 1 ? `${percentual}%` : null) : f.valor > 0 ? formatarMoeda(f.valor, moeda) : null;
  // A previa so conta o tempo do fim que foi digitado e ainda nao passou; a contagem da semana e do ano o servico faz.
  const fim = f.vigencia === "PERIODO" ? localParaIso(f.termina) : null;
  const terminaEm = fim && Date.parse(fim) > Date.now() ? fim : null;
  // Os planos ativos e os que ja estavam marcados (um plano desativado depois continua aparecendo pra poder tirar).
  const planos = catalogo.filter((c) => c.ativo || f.planos.includes(c.slug));
  return (
    <>
      <Pilha espaco={2}>
        <span className="mtc-rotulo">Prévia do destaque no checkout</span>
        <DestaqueDaPromocao nome={f.nome.trim()} chamada={f.chamada.trim() || null} tema={f.tema} desconto={desconto} terminaEm={terminaEm} />
      </Pilha>
      <Campo rotulo="Nome" valor={f.nome} aoMudar={(v) => mudar({ nome: v })} maxLength={60} placeholder="Ex.: Black Friday" dica="Aparece em destaque no checkout, nos planos à venda e em Vendas." required />
      <Campo rotulo="Chamada" valor={f.chamada} aoMudar={(v) => mudar({ chamada: v })} maxLength={120} contador placeholder="Ex.: O maior desconto do ano, só até segunda." dica="Opcional. Uma frase curta embaixo do nome." />
      <CampoSelecao rotulo="Tema" valor={f.tema} aoMudar={(v) => mudar({ tema: v })} opcoes={TEMAS_DA_PROMOCAO.map((t) => ({ valor: t, rotulo: NOME_DO_TEMA[t] }))} dica="A cara do destaque. Noite é a da Black Friday." />
      <Segmentado rotulo="Tipo de desconto" nome="tipo-da-promocao" valor={f.tipo} aoMudar={(v) => mudar({ tipo: v })} opcoes={[{ valor: "PERCENTUAL", rotulo: "Porcentagem" }, { valor: "VALOR", rotulo: "Valor fixo" }]} />
      {f.tipo === "PERCENTUAL" ? (
        <Campo rotulo="Porcentagem" valor={f.percentual} aoMudar={(v) => mudar({ percentual: soDigitos(v).slice(0, 2) })} inputMode="numeric" mono dica="De 1 a 90, sobre o preço do plano na plataforma escolhida. Os benefícios ficam no preço cheio." />
      ) : (
        <CampoDinheiro rotulo="Valor" centavos={f.valor} aoMudar={(v) => mudar({ valor: v })} moeda={moeda} dica={moeda === "USD" ? "Em dólar, como a vitrine. O real cobrado sai pela cotação de cada checkout." : "Tirado do preço do plano. O preço nunca fica abaixo de R$ 1,00."} />
      )}
      <Segmentado rotulo="Quando vale" nome="vigencia-da-promocao" valor={f.vigencia} aoMudar={(v) => mudar({ vigencia: v })} opcoes={[{ valor: "PERIODO", rotulo: "Período" }, { valor: "SEMANAL", rotulo: "Toda semana" }, { valor: "ANUAL", rotulo: "Todo ano" }]} />
      {f.vigencia === "PERIODO" && (
        // O campo de data e hora tem largura propria (o navegador nao encolhe): lado a lado so com espaco pros dois.
        <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))" }}>
          <CampoDataHora rotulo="Começa em" valor={f.comeca} aoMudar={(v) => mudar({ comeca: v })} dica="Hora de Brasília. Vazio: já começa." />
          <CampoDataHora rotulo="Termina em" valor={f.termina} aoMudar={(v) => mudar({ termina: v })} dica="Vazio: até alguém pausar ou excluir." />
        </div>
      )}
      {f.vigencia === "SEMANAL" && (
        <Pilha espaco={3}>
          <div role="group" aria-label="Dias da semana" className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: "var(--e-1) var(--e-3)" }}>
            {DIAS_DA_SEMANA.map((nome, d) => (
              <CampoCaixa key={nome} marcado={f.dias.includes(d)} aoMudar={(v) => mudar({ dias: v ? [...f.dias, d] : f.dias.filter((x) => x !== d) })}>{nome}</CampoCaixa>
            ))}
          </div>
          <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 160px), 1fr))" }}>
            <CampoHora rotulo="Das" valor={f.das} aoMudar={(v) => mudar({ das: v })} dica="Hora de Brasília. Os dois vazios: o dia todo." />
            <CampoHora rotulo="Até" valor={f.ate} aoMudar={(v) => mudar({ ate: v })} />
          </div>
        </Pilha>
      )}
      {f.vigencia === "ANUAL" && (
        <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 160px), 1fr))" }}>
          <Campo rotulo="De" valor={f.deDia} aoMudar={(v) => mudar({ deDia: mascaraDdmm(v) })} inputMode="numeric" mono placeholder="dd/mm" dica="Dia e mês. Vale o dia inteiro." />
          <Campo rotulo="Até" valor={f.ateDia} aoMudar={(v) => mudar({ ateDia: mascaraDdmm(v) })} inputMode="numeric" mono placeholder="dd/mm" dica="Pode virar o ano: 20/12 a 05/01." />
        </div>
      )}
      <Pilha espaco={2}>
        <span className="mtc-rotulo">Planos</span>
        <CampoCaixa marcado={f.todosOsPlanos} aoMudar={(v) => mudar({ todosOsPlanos: v })}>Todos os planos</CampoCaixa>
        {f.todosOsPlanos ? (
          <span className="mtc-campo__dica">Vale em todo checkout de plano, inclusive dos planos criados depois. Checkout sem assinatura nunca tem promoção.</span>
        ) : carregandoPlanos ? (
          <Carregando compacto texto="Buscando os planos" />
        ) : planos.length === 0 ? (
          <span className="mtc-campo__dica">Nenhum plano ativo. Crie em Cadastros, Planos.</span>
        ) : (
          <div role="group" aria-label="Planos da promoção" className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))", gap: "var(--e-1) var(--e-3)" }}>
            {planos.map((c) => (
              <CampoCaixa key={c.slug} marcado={f.planos.includes(c.slug)} aoMudar={(v) => mudar({ planos: v ? [...f.planos, c.slug] : f.planos.filter((x) => x !== c.slug) })}>{c.nome}</CampoCaixa>
            ))}
          </div>
        )}
      </Pilha>
      <Segmentado rotulo="Para quem" nome="publico-da-promocao" valor={f.soClientes ? "CPFS" : "TODOS"} aoMudar={(v) => mudar({ soClientes: v === "CPFS" })} opcoes={[{ valor: "TODOS", rotulo: "Todos os clientes" }, { valor: "CPFS", rotulo: "Só estes CPFs" }]} />
      {f.soClientes && (
        <CampoTextoLongo rotulo="CPFs" valor={f.cpfs} aoMudar={(v) => mudar({ cpfs: v })} linhas={4} placeholder={"529.982.247-25\n111.444.777-35"} dica="Um por linha. A promoção só aparece depois que o cliente entra na conta, vale uma compra por CPF e não sai na API do site." />
      )}
    </>
  );
}

export function PaginaPromocoes() {
  const pag = usePagina("pagina");
  const { ler, postar, por, apagar } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const admin = pode("ADMIN");
  const lista = useQuery({ queryKey: ["promocoes"], queryFn: () => ler<{ itens: Promocao[]; moeda: Moeda }>(PAG("/v1/admin/promocoes")) });
  const catalogo = useQuery({ queryKey: ["planos"], queryFn: () => ler<{ itens: PlanoDoCatalogo[] }>("/v1/admin/planos") });
  const moeda: Moeda = lista.data?.moeda === "USD" ? "USD" : "BRL";
  const planos = catalogo.data?.itens ?? [];
  const [novo, setNovo] = useState(false);
  const [editando, setEditando] = useState<Promocao | null>(null);
  const [excluindo, setExcluindo] = useState<Promocao | null>(null);
  const [mexendo, setMexendo] = useState<string | null>(null);
  const [f, setF] = useState<FormularioDaPromocao>(FORMULARIO_VAZIO);
  const [feito, setFeito] = useState<string>();
  const [erro, setErro] = useState<string>();
  const recarregar = () => qc.invalidateQueries({ queryKey: ["promocoes"] });
  const limpar = () => { setFeito(undefined); setErro(undefined); };

  // Pausar e retomar nao tem confirmacao: e so voltar atras. Excluir tem (o dialogo do painel, nunca o do navegador).
  const pausar = async (p: Promocao) => {
    limpar();
    setMexendo(p.id);
    try {
      await postar(PAG(`/v1/admin/promocoes/${p.id}/pausada`), { pausada: !p.pausada });
      await recarregar();
      setFeito(p.pausada ? "Promoção retomada." : "Promoção pausada.");
    } catch (err) {
      setErro(erroDe(err));
    } finally {
      setMexendo(null);
    }
  };

  const colunas: Coluna<Promocao>[] = [
    { chave: "nome", rotulo: "Promoção", celula: (p) => <><strong>{p.nome}</strong>{p.chamada && <span className="mtc-tabela__apoio">{p.chamada}</span>}<span className="mtc-tabela__apoio">Tema {NOME_DO_TEMA[p.tema as keyof typeof NOME_DO_TEMA] ?? p.tema}</span></> },
    { chave: "desconto", rotulo: "Desconto", celula: (p) => <span className="mtc-mono">{descontoDa(p, moeda)}</span> },
    { chave: "quando", rotulo: "Quando vale", celula: (p) => quandoVale(p) },
    { chave: "planos", rotulo: "Planos", celula: (p) => { const x = planosDa(p, planos); return <>{x.texto}{x.apoio && <span className="mtc-tabela__apoio">{x.apoio}</span>}</>; } },
    { chave: "publico", rotulo: "Público", celula: (p) => (p.so_clientes ? `${p.clientes.length} ${p.clientes.length === 1 ? "cliente" : "clientes"}` : "Todos os clientes") },
    {
      chave: "situacao",
      rotulo: "Situação",
      celula: (p) => {
        const s = SITUACAO[p.situacao] ?? { rotulo: p.situacao, tom: "neutro" as const };
        return <><Selo tom={s.tom}>{s.rotulo}</Selo>{p.situacao === "VALENDO" && p.termina_agora_em && <span className="mtc-tabela__apoio">Até {dataHoraEmBrasilia(p.termina_agora_em)}</span>}</>;
      },
    },
    { chave: "vendas", rotulo: "Vendas", alinhar: "direita", celula: (p) => <>{p.vendas.pagas} {p.vendas.pagas === 1 ? "venda" : "vendas"}<span className="mtc-tabela__apoio">{formatarReais(p.vendas.desconto_centavos)} de desconto</span></> },
    ...(admin
      ? [{
          chave: "acoes",
          rotulo: "Ações",
          rotuloOculto: true,
          celula: (p: Promocao) => (
            <span className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "wrap" }}>
              <Botao tamanho="pequeno" variante="secundario" onClick={() => { limpar(); setF(formularioDe(p)); setEditando(p); }}>Editar</Botao>
              {p.situacao !== "ENCERRADA" && <Botao tamanho="pequeno" variante="fantasma" carregando={mexendo === p.id} onClick={() => void pausar(p)}>{p.pausada ? "Retomar" : "Pausar"}</Botao>}
              <Botao tamanho="pequeno" variante="fantasma" onClick={() => { limpar(); setExcluindo(p); }}>Excluir</Botao>
            </span>
          ),
        }]
      : []),
  ];

  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina
        titulo="Promoções"
        descricao="O preço já aparece com desconto no checkout e nos planos à venda, sem código. Vale nos planos que você escolher, num período, toda semana ou todo ano, pra todos os clientes ou só pra alguns CPFs. Não soma com cupom nem com outra promoção: vale o maior desconto."
        acoes={admin ? <Botao onClick={() => { limpar(); setF(FORMULARIO_VAZIO); setNovo(true); }}>Nova promoção</Botao> : undefined}
      />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {!admin && <Aviso tipo="info">Criar e mudar promoção pede o acesso de ADMIN no painel. Você pode ver a lista e as vendas.</Aviso>}
      {lista.isLoading ? <Carregando compacto texto="Buscando as promoções" /> : lista.isError ? <Aviso tipo="erro">{erroDe(lista.error)}</Aviso> : (
        <Tabela
          paginacao={pag}
          linhas={lista.data?.itens ?? []}
          chave={(p) => p.id}
          cartao="media"
          vazio={<Vazio titulo="Nenhuma promoção">Crie a primeira, como a Black Friday, e veja a prévia do destaque antes de salvar.</Vazio>}
          colunas={colunas}
        />
      )}

      <DialogoDeAcao
        aberto={novo}
        aoFechar={() => setNovo(false)}
        titulo="Nova promoção"
        descricao="Quem se encaixa vê o preço com desconto no checkout e nos planos à venda, com o destaque abaixo."
        botao="Criar promoção"
        podeEnviar={f.nome.trim().length >= 2}
        aoEnviar={() => postar(PAG("/v1/admin/promocoes"), corpoDaPromocao(f))}
        aoConcluir={() => { void recarregar(); setFeito("Promoção criada."); }}
      >
        <CamposDaPromocao f={f} setF={setF} moeda={moeda} catalogo={planos} carregandoPlanos={catalogo.isLoading} />
      </DialogoDeAcao>

      <DialogoDeAcao
        aberto={Boolean(editando)}
        aoFechar={() => setEditando(null)}
        titulo={`Promoção ${editando?.nome ?? ""}`}
        descricao="Vale dali pra frente: quem já está no checkout vê o valor novo na hora de pagar. As vendas feitas continuam como estão."
        botao="Salvar"
        podeEnviar={f.nome.trim().length >= 2}
        aoEnviar={() => por(PAG(`/v1/admin/promocoes/${editando!.id}`), corpoDaPromocao(f))}
        aoConcluir={() => { void recarregar(); setEditando(null); setFeito("Promoção salva."); }}
      >
        <CamposDaPromocao f={f} setF={setF} moeda={moeda} catalogo={planos} carregandoPlanos={catalogo.isLoading} />
      </DialogoDeAcao>

      <DialogoDeAcao
        aberto={Boolean(excluindo)}
        aoFechar={() => setExcluindo(null)}
        titulo={`Excluir ${excluindo?.nome ?? "a promoção"}`}
        descricao="Ela sai da lista e do checkout na hora. As vendas feitas com ela continuam dizendo qual promoção deu o desconto."
        botao="Excluir"
        variante="perigo"
        aoEnviar={() => apagar(PAG(`/v1/admin/promocoes/${excluindo!.id}`))}
        aoConcluir={() => { void recarregar(); setExcluindo(null); setFeito("Promoção excluída."); }}
      />
    </Pilha>
  );
}
